import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Drawer, IconButton, Typography } from '@mui/material';
import type { ReactNode } from 'react';

const paperSx = {
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
} as const;

const headerBoxSx = {
  p: 2,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
} as const;

const headerActionsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
} as const;

const footerBoxSx = {
  p: 2,
  flexShrink: 0,
} as const;

export const drawerContentSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
} as const;

type BaseDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  headerAction?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: number;
  closeAriaLabel?: string;
  /** Keeps the content in the DOM while closed; MUI's advice for drawers opened often on phones. */
  keepMounted?: boolean;
};

export function BaseDrawer({
  open,
  onClose,
  title,
  headerAction,
  footer,
  children,
  width = 400,
  closeAriaLabel = 'close',
  keepMounted,
}: BaseDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted }}
      PaperProps={{ elevation: 2, sx: { ...paperSx, width: { xs: '100%', sm: width } } }}
    >
      <Box sx={headerBoxSx}>
        {typeof title === 'string' ? <Typography variant="h5">{title}</Typography> : title}
        <Box sx={headerActionsSx}>
          {headerAction}
          <IconButton size="small" onClick={onClose} aria-label={closeAriaLabel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      {children}

      {footer != null && (
        <>
          <Divider />
          <Box sx={footerBoxSx}>{footer}</Box>
        </>
      )}
    </Drawer>
  );
}
